'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: string;
}

export default function ProcessSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const steps: ProcessStep[] = [
    {
      id: 'step_1',
      number: '01',
      title: 'Discovery',
      description: 'We analyze your requirements and understand your business goals',
      icon: 'MagnifyingGlassIcon',
    },
    {
      id: 'step_2',
      number: '02',
      title: 'Planning',
      description: 'Create a detailed roadmap and strategy for your project',
      icon: 'DocumentTextIcon',
    },
    {
      id: 'step_3',
      number: '03',
      title: 'Implementation',
      description: 'Execute the solution with precision and attention to detail',
      icon: 'WrenchScrewdriverIcon',
    },
    {
      id: 'step_4',
      number: '04',
      title: 'Support',
      description: 'Ongoing maintenance and support to ensure continued success',
      icon: 'ChatBubbleLeftRightIcon',
    },
  ];

  return (
    <section className="py-20 lg:py-32 relative z-10 bg-gray-900 border-y border-zinc-800">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16 animate-on-scroll animate-in">
          <span className="text-xs uppercase tracking-[0.4em] text-blue-400 mb-4 block">
            Our Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            How We Work
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            A proven methodology that ensures successful project delivery
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`relative animate-on-scroll animate-in-delay-${index + 1}`}
            >
              <div className="bg-gray-950 border border-zinc-800 rounded-xl p-8 hover:border-blue-500 transition-all duration-300 h-full">
                <div className="text-6xl font-bold text-blue-500/20 mb-4">
                  {step.number}
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 mb-6">
                  <Icon name={step.icon as any} size={24} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>

              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px bg-zinc-700"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}